import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Snackbar, Alert } from '@mui/material';
import { useUsers } from '../items/hooks/HUsers';
import { useObjectsTab } from '../items/hooks/HObjects';
import { useDocs } from '../items/hooks/HDocs';
import { usePermissions } from '../items/hooks/HPermissions';
import { toBase64, downloadBlobFromUrl, createPdfSubmission, getSubmission, getSubmissionDocuments, uploadSignedDocument } from '../utils/Nda';
import { User } from '../items/interfaces/IUsers';
import { API_BASE } from '../../config/constants';

interface ObjectAccessDialogProps {
    open: boolean;
    objectId: string | number | null;
    user?: User | null;
    onClose: () => void;
    onSubmit: () => void;
}

const ObjectAccessDialog: React.FC<ObjectAccessDialogProps> = ({
    open,
    objectId,
    user,
    onClose,
    onSubmit,
}) => {
    const { createUserObjectRequest } = useUsers();
    const { fetchObjectDocs } = useObjectsTab();
    const { uploadUserDocumentToAzure } = useDocs();
    const { permissions, loadPermissions, createPermission } = usePermissions(user?.id ?? user?.user_id ?? null);

    const [objectNdaFlag, setObjectNdaFlag] = useState<boolean>(false);
    const [ndaDocUrl, setNdaDocUrl] = useState<string | null>(null);
    const [ndaSubmissionId, setNdaSubmissionId] = useState<number | null>(null);
    const [ndaProcessing, setNdaProcessing] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('info');

    const showSnackbar = useCallback((msg: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => {
        setSnackbarMsg(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    const [ndaSatisfied, setNdaSatisfied] = useState(false);
    const [thanksOpen, setThanksOpen] = useState(false);
    const submitButtonRef = useRef<HTMLButtonElement | null>(null);
    const thanksSubmitRef = useRef<HTMLButtonElement | null>(null);

    const loadObjectData = useCallback(async () => {
        if (!objectId) return;
        try {
            const docs = await fetchObjectDocs(objectId);
            const ndaDoc = Array.isArray(docs)
                ? docs.find((d: any) => String(d?.name ?? '').toLowerCase().includes('nda') && String(d?.name ?? '').toLowerCase().endsWith('.pdf'))
                : null;
            const fallbackNdaUrl = `https://xmarketstorage.blob.core.windows.net/docs/objects/${objectId}/NDA.pdf`;
            setNdaDocUrl(ndaDoc?.url ?? fallbackNdaUrl);

            const resp = await fetch(`${API_BASE}/api/objects/get-all-objects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (resp.ok) {
                const payload = await resp.json().catch(() => ({}));
                const allObjects = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
                const found = allObjects.find((o: any) => String(o.id) === String(objectId));
                const ndaValRaw = found?.nda ?? found?.NDA ?? false;
                const ndaValStr = String(ndaValRaw).toLowerCase();
                setObjectNdaFlag(ndaValRaw === true || ndaValRaw === 1 || ndaValStr === 'true' || ndaValStr === '1');
            }
        } catch (err) {
            console.warn('[ObjectAccessDialog] failed to load NDA or object data', err);
        }
    }, [objectId, fetchObjectDocs]);

    const handleSubmit = async () => {
        const userId = user?.id ?? user?.user_id;
        console.log('[ObjectAccessDialog] submit clicked', { user, userId, objectId });

        if (userId == null || objectId == null) {
            console.warn('[ObjectAccessDialog] submit aborted due to missing identifiers', { userId, objectId });
            return;
        }

        try {
            const result = await createUserObjectRequest({
                request_id: 1,
                user_id: userId,
                object_id: objectId,
                description: 'Request Access',
            });

            console.log('[ObjectAccessDialog] request created', result);
            onSubmit();
        } catch (err) {
            console.error('[ObjectAccessDialog] request creation failed', err);
        }
    };

    useEffect(() => {
        if (!open) return;
        void loadObjectData();
        // reset ndaSatisfied when opening
        setNdaSatisfied(false);
    }, [open, loadObjectData]);

    // ensure we have the latest permissions for the user when dialog opens
    useEffect(() => {
        if (!open) return;
        void loadPermissions();
    }, [open, loadPermissions]);

    // log a consolidated permissions snapshot when dialog opens or permissions change
    useEffect(() => {
        if (!open) return;
        try {
            const keysSet = new Set<string>();
            const samples: any[] = [];
            if (Array.isArray(permissions)) {
                permissions.slice(0, 5).forEach((p: any) => {
                    samples.push(p);
                    Object.keys(p || {}).forEach((k) => keysSet.add(k));
                });
            }
            const keys = Array.from(keysSet).sort();
            console.log('[ObjectAccessDialog] Permissions snapshot:', { count: Array.isArray(permissions) ? permissions.length : 0, keys, samples });
        } catch (err) {
            console.warn('[ObjectAccessDialog] Failed to log permissions snapshot', err);
        }
    }, [open, permissions]);

    // signing flow initiated from header or other UI; verify step handled below
    const handleSignNda = async () => {
        const currentUserId = user?.id ?? user?.user_id ?? null;
        if (!currentUserId) {
            showSnackbar('User id is missing. Please re-login and try again.', 'error');
            return;
        }

        if (!ndaDocUrl) {
            showSnackbar('NDA document not found for this object.', 'error');
            return;
        }

        const signerEmail = String(user?.email ?? user?.user ?? '').trim();
        const signerName =
            String(user?.name ?? '').trim() || [String(user?.first_name ?? '').trim(), String(user?.last_name ?? '').trim()].filter(Boolean).join(' ').trim() || signerEmail;

        setNdaProcessing(true);
        try {
            const ndaBlob = await downloadBlobFromUrl(String(ndaDocUrl));
            const ndaBase64 = await toBase64(ndaBlob);

            const created = await createPdfSubmission(API_BASE, ndaBase64, signerName, signerEmail);
            const submissionId = Number(created?.id ?? created?.submission_id ?? created?.submissionId ?? created?.submission_id);
            const embedSrc = created?.submitters?.[0]?.embed_src ?? created?.embed_src;
            setNdaSubmissionId(submissionId);

            if (embedSrc) window.open(embedSrc, '_blank', 'noopener,noreferrer');
            showSnackbar('NDA signing session created. Complete signature, then click Verify NDA to save.', 'info');
        } catch (err: any) {
            showSnackbar(err?.message || 'Failed to create NDA signing flow.', 'error');
        } finally {
            setNdaProcessing(false);
        }
    };

    const finalizeNda = useCallback(async (submissionId: number | null) => {
        if (!submissionId) {
            console.log('[ObjectAccessDialog] finalizeNda called with no submissionId');
            showSnackbar('No NDA submission to verify.', 'warning');
            return;
        }

        console.log('[ObjectAccessDialog] finalizeNda starting', { submissionId });
        setNdaProcessing(true);
        try {
            const submission = await getSubmission(API_BASE, submissionId);
            const submissionStatus = submission?.status;
            console.log('[ObjectAccessDialog] submission status', { submissionId, submissionStatus });
            if (submissionStatus !== 'completed') {
                console.log('[ObjectAccessDialog] submission not completed', { submissionId, submissionStatus });
                showSnackbar('NDA is not completed yet. Please sign it first, then try again.', 'warning');
                return;
            }

            const docs = await getSubmissionDocuments(API_BASE, submissionId);
            const signedDoc = docs?.documents?.[0];
            if (!signedDoc) throw new Error('Could not retrieve signed NDA document.');

            console.log('[ObjectAccessDialog] retrieved signed document metadata', { signedDoc });
            const signedBlob = await downloadBlobFromUrl(String(signedDoc.url));

            const uploadFileName = `NDA_${String(objectId)}_${String(user?.id ?? user?.user_id ?? 0)}.pdf`;
            console.log('[ObjectAccessDialog] uploading signed NDA', { uploadFileName });
            const saved = await uploadSignedDocument(uploadUserDocumentToAzure, signedBlob, uploadFileName, user?.id ?? user?.user_id ?? 0);
            if (!saved) throw new Error('NDA was signed but failed to save.');

            console.log('[ObjectAccessDialog] signed NDA saved', { uploadFileName, saved });
            showSnackbar('NDA signed and saved successfully.', 'success');
            setNdaSubmissionId(null);

            // After saving signed NDA, reload object data and enable submit for this dialog session
            try {
                try {
                    await loadObjectData();
                } catch (e) {
                    console.warn('[ObjectAccessDialog] failed to reload object data after NDA finalize', e);
                }
                setNdaSatisfied(true);
                setThanksOpen(true);
            } catch (err) {
                console.error('[ObjectAccessDialog] post-finalize handling failed', err);
            }
        } catch (err: any) {
            console.error('[ObjectAccessDialog] finalizeNda error', err);
            showSnackbar(err?.message || 'Failed to verify and save NDA.', 'error');
        } finally {
            setNdaProcessing(false);
        }
    }, [uploadUserDocumentToAzure, createPermission, user, objectId, showSnackbar, loadObjectData]);

    // When the window regains focus, attempt to finalize pending NDA submission
    useEffect(() => {
        const onFocus = () => {
            if (ndaSubmissionId) {
                console.log('[ObjectAccessDialog] window focused — attempting finalize', { ndaSubmissionId });
                void finalizeNda(ndaSubmissionId);
            }
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [ndaSubmissionId, finalizeNda]);

    // when thanks dialog opens, focus its Submit button so user can act immediately
    useEffect(() => {
        if (thanksOpen) {
            try {
                // prefer the submit inside the thanks dialog
                thanksSubmitRef.current?.focus();
            } catch (err) {
                console.debug('[ObjectAccessDialog] failed to focus thanks dialog submit', err);
            }
        }
    }, [thanksOpen]);

    const userRoleId = user?.role_id;

    const ndaRaw = (user as any)?.nda;
    const userNda =
        ndaRaw === true ||
        (typeof ndaRaw === 'number' && ndaRaw === 1) ||
        (typeof ndaRaw === 'string' && (ndaRaw.toLowerCase() === 'true' || ndaRaw === '1'));

    const isPendingRole = userRoleId === 4;

    const messageContent = (
        <>
            {isPendingRole ? (
                // If the user hasn't acknowledged/recorded NDA at account level, show only the instruction

                    <span>Waiting for user approval</span>
            ) : (
                <span>Submit to request access to the object</span>
            )}

            {/* If object requires NDA, show inline error message */}
            {objectNdaFlag && (
                <Typography color='error' sx={{ mt: 1 }}>This object requires an NDA. You must sign it before requesting access.</Typography>
            )}
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
            <DialogTitle>Request Access</DialogTitle>
            <DialogContent>
                <Typography>
                    {messageContent}
                    {ndaSubmissionId ? (
                        <Button size='small' onClick={() => void finalizeNda(ndaSubmissionId)} sx={{ ml: 1 }}>Verify NDA</Button>
                    ) : null}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {objectNdaFlag && !ndaSatisfied ? (
                    <Button onClick={() => void handleSignNda()} variant='contained' color='primary' disabled={ndaProcessing}>
                        {ndaProcessing ? 'Processing NDA...' : 'Sign NDA'}
                    </Button>
                ) : (
                    <Button
                        onClick={() => void handleSubmit()}
                        variant='contained'
                        color='primary'
                        disabled={userRoleId === 4 && !ndaSatisfied}
                        ref={submitButtonRef}
                    >
                        Submit
                    </Button>
                )}
            </DialogActions>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
            <Dialog open={thanksOpen} onClose={() => setThanksOpen(false)}>
                <DialogTitle>Thanks for signing</DialogTitle>
                <DialogContent>
                    <Typography>Thank you for signing the NDA. You may now submit your access request.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setThanksOpen(false)}>Close</Button>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={async () => {
                            await handleSubmit();
                            setThanksOpen(false);
                            try {
                                onClose();
                            } catch (err) {
                                console.debug('[ObjectAccessDialog] onClose after submit failed', err);
                            }
                        }}
                        disabled={userRoleId === 4 && !ndaSatisfied}
                        ref={thanksSubmitRef}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default ObjectAccessDialog;
